class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.defaultExperience = 'expanded';
    this.autoClose = this.getAttribute('cart-auto-close') === 'true';
    this.autoCloseDelay = Number(this.getAttribute('cart-notification-timer')) || 5000;
    this.autoCloseTimeout = null;
    this._countdownActive = false;
    this._lineChangePending = false;
    this._experienceLocked = false;
    this._experienceObserver = null;
    this._thresholdData = null;
    this._thresholdInitialized = false;
    this._cartStatePromise = null;
    this._cartDirty = false;
    this._openSource = null;
    this._thresholdDetailsOpen = false;
    this._thresholdDetailsObserver = null;
    this._thresholdDetailsFallback = null;

    this.notification = this.querySelector('#cart-notification');
    if (!this.notification) return;

    this.timerBar = this.notification.querySelector('.cart-notification__timer-bar');
    this.toggleElement = document.getElementById('cart-icon-bubble-header') || document.querySelector('.header__icon--cart');
    this.cartLink = document.getElementById('cart-icon-open') || this.toggleElement?.querySelector('a[href]');
    this.closeIcon = document.getElementById('cart-icon-close');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onViewportChange = this.scheduleMobilePositionUpdate.bind(this);
    this.onThresholdDetailsMouseEnter = this.handleThresholdDetailsMouseEnter.bind(this);
    this.onThresholdDetailsMouseLeave = this.handleThresholdDetailsMouseLeave.bind(this);
    this._mobilePositionFrame = null;
    this._thresholdDetailsHoverModal = null;

    this.initializeExperience();
    this.bindEvents();
  }

  initializeExperience() {
    const storedExperience = this.readStoredExperience();
    const assignedExperience = this.readAssignedExperience();

    if (storedExperience) {
      this.setExperience(storedExperience, true);
      return;
    }

    if (assignedExperience) {
      this.setExperience(assignedExperience);
    } else {
      // Use the configured provisional default while Webflow completes its
      // asynchronous assignment.
      this.experience = this.defaultExperience;
      this.dataset.experience = this.defaultExperience;
      this.applyExperienceUI();
    }

    // Keep observing Webflow until the first cart interaction/add. That
    // interaction permanently latches the value present at that moment, or
    // base when Webflow has not supplied a valid value.
    this.dataset.experiencePending = '';

    this._experienceObserver = new MutationObserver(() => {
      const experience = this.readAssignedExperience();
      this.setExperience(experience || this.defaultExperience);
    });
    this._experienceObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-cart-experience'],
    });
  }

  bindEvents() {
    if (this.toggleElement) {
      this.toggleElement.addEventListener('mouseenter', (event) => this.handleToggleMouseEnter(event));
      this.toggleElement.addEventListener('mouseleave', (event) => this.handleToggleMouseLeave(event));
      this.toggleElement.addEventListener('focusin', (event) => this.handleToggleFocusIn(event));
      this.toggleElement.addEventListener('focusout', (event) => this.handleToggleFocusOut(event));
      this.toggleElement.addEventListener('click', (event) => this.handleToggleClick(event));
    }

    if (this.closeIcon) {
      this.closeIcon.setAttribute('aria-hidden', 'true');
      this.closeIcon.setAttribute('tabindex', '-1');
      this.closeIcon.addEventListener('keydown', (event) => this.handleCloseIconKeydown(event));
    }

    this.notification.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close({ restoreFocus: true });
    });
    this.notification.addEventListener('touchstart', () => this.pauseAutoClose(), { passive: true });
    this.notification.addEventListener('touchend', () => this.resumeAutoClose(), { passive: true });
    this.notification.addEventListener('touchcancel', () => this.resumeAutoClose(), { passive: true });
    this.notification.addEventListener('click', (event) => this.handleNotificationClick(event));

    window.addEventListener('scroll', this.onViewportChange, { passive: true });
    window.addEventListener('resize', this.onViewportChange, { passive: true });
    window.visualViewport?.addEventListener('resize', this.onViewportChange, { passive: true });

    this.bindThresholdDetailsHover(document.getElementById('os-modal-desktop'));
  }

  readAssignedExperience() {
    const value = document.documentElement.dataset.cartExperience;
    return value === 'base' || value === 'expanded' ? value : null;
  }

  readStoredExperience() {
    try {
      const value = window.sessionStorage.getItem('dermalogicaCartExperience');
      return value === 'base' || value === 'expanded' ? value : null;
    } catch (error) {
      return null;
    }
  }

  storeExperience(experience) {
    try {
      window.sessionStorage.setItem('dermalogicaCartExperience', experience);
    } catch (error) {
      // Storage can be unavailable in privacy modes; the in-memory latch still
      // keeps the experience stable for the current page.
    }
  }

  setExperience(experience, lock = false) {
    const resolved = experience === 'base' || experience === 'expanded'
      ? experience
      : this.defaultExperience;
    if (this._experienceLocked && this.experience !== resolved) return this.experience;

    this.experience = resolved;
    this.dataset.experience = resolved;
    this.applyExperienceUI();

    if (lock) {
      this._experienceLocked = true;
      delete this.dataset.experiencePending;
      this.storeExperience(resolved);
      this._experienceObserver?.disconnect();
      this._experienceObserver = null;
    }

    if (resolved === 'expanded' && this._experienceLocked) {
      this.initializeThreshold();
      if (this._cartDirty) this.syncExpandedContents();
    }
    return resolved;
  }

  lockExperience() {
    if (this._experienceLocked) return this.experience;
    return this.setExperience(this.readAssignedExperience() || this.defaultExperience, true);
  }

  getExperience(lock = false) {
    return lock ? this.lockExperience() : this.experience || this.defaultExperience;
  }

  isExpanded(lock = false) {
    return this.getExperience(lock) === 'expanded';
  }

  usesMobileCartInteraction() {
    return window.matchMedia('(max-width: 749px)').matches
      || !window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  applyExperienceUI() {
    const expanded = this.experience === 'expanded';

    this.querySelectorAll('[data-base-only]').forEach((element) => {
      element.hidden = expanded;
    });
    this.querySelectorAll('[data-expanded-only]').forEach((element) => {
      element.hidden = !expanded;
    });
    this.querySelectorAll('[data-expanded-control]').forEach((element) => {
      element.disabled = !expanded;
    });

    const lines = this.getCartLines();
    if (expanded) {
      lines.forEach((line) => {
        line.hidden = false;
      });
    } else if (!this.notification.classList.contains('active')) {
      lines.forEach((line) => {
        line.hidden = true;
      });
    }

    if (this.cartLink) {
      if (expanded) {
        this.cartLink.setAttribute('aria-haspopup', 'dialog');
      } else {
        this.cartLink.removeAttribute('aria-haspopup');
      }
      this.cartLink.setAttribute('aria-expanded', String(this.notification.classList.contains('active')));
    }
  }

  handleToggleMouseEnter() {
    if (this.usesMobileCartInteraction()) return;

    this.pauseAutoClose();
    if (!this.isExpanded(true)) return;
    if (
      this.notification.classList.contains('active')
      && this.notification.querySelector('.cart-item--just-added')
    ) return;

    this.setActiveElement(this.cartLink || this.toggleElement);
    this.open({ source: 'hover' });
  }

  handleToggleMouseLeave(event) {
    if (this.usesMobileCartInteraction()) return;
    if (event.relatedTarget && this.toggleElement?.contains(event.relatedTarget)) return;
    if (this.isThresholdDetailsTarget(event.relatedTarget)) {
      this.pauseAutoClose();
      return;
    }

    this.resumeAutoClose();
    if (!this.isExpanded()) return;
    if (this._openSource === 'hover') this.close({ restoreFocus: false });
  }

  handleToggleFocusIn(event) {
    if (
      this.usesMobileCartInteraction()
      || event.target !== this.cartLink
      || !this.isExpanded(true)
    ) return;

    this.setActiveElement(this.cartLink);
    this.open({ source: 'keyboard' });
  }

  handleToggleFocusOut(event) {
    if (
      this.usesMobileCartInteraction()
      || !this.isExpanded()
      || this._openSource !== 'keyboard'
    ) return;

    if (
      event.relatedTarget
      && (
        this.toggleElement?.contains(event.relatedTarget)
        || this.isThresholdDetailsTarget(event.relatedTarget)
      )
    ) return;
    this.close({ restoreFocus: false });
  }

  bindThresholdDetailsHover(modal) {
    if (!modal || this._thresholdDetailsHoverModal === modal) return;

    if (this._thresholdDetailsHoverModal) {
      this._thresholdDetailsHoverModal.removeEventListener('mouseenter', this.onThresholdDetailsMouseEnter);
      this._thresholdDetailsHoverModal.removeEventListener('mouseleave', this.onThresholdDetailsMouseLeave);
    }

    modal.addEventListener('mouseenter', this.onThresholdDetailsMouseEnter);
    modal.addEventListener('mouseleave', this.onThresholdDetailsMouseLeave);
    this._thresholdDetailsHoverModal = modal;
  }

  isThresholdDetailsTarget(target) {
    const modal = this._thresholdDetailsHoverModal || document.getElementById('os-modal-desktop');
    return Boolean(this._thresholdDetailsOpen && target?.nodeType && modal?.contains(target));
  }

  handleThresholdDetailsMouseEnter() {
    if (!this._thresholdDetailsOpen || this.usesMobileCartInteraction()) return;
    this.pauseAutoClose();
  }

  handleThresholdDetailsMouseLeave(event) {
    if (!this._thresholdDetailsOpen || this.usesMobileCartInteraction()) return;
    if (
      event.relatedTarget
      && (
        this.toggleElement?.contains(event.relatedTarget)
        || this.isThresholdDetailsTarget(event.relatedTarget)
      )
    ) return;

    this.resumeAutoClose();
    if (this.isExpanded() && this._openSource === 'hover') {
      this.close({ restoreFocus: false });
    }
  }

  handleToggleClick(event) {
    if (this.notification.contains(event.target)) return;

    const expanded = this.isExpanded(true);
    const clickedClose = event.target.closest('#cart-icon-close');
    const clickedLink = event.target.closest('#cart-icon-open');

    if (clickedClose?.classList.contains('active') && !expanded) {
      event.preventDefault();
      this.close({ restoreFocus: true });
      return;
    }

    if (!expanded || !this.usesMobileCartInteraction()) {
      if (!clickedLink && this.cartLink?.href) window.location.assign(this.cartLink.href);
      return;
    }

    // Expanded touch behavior: first tap opens, second tap follows /cart.
    if (!this.notification.classList.contains('active')) {
      event.preventDefault();
      this.setActiveElement(this.cartLink || this.toggleElement);
      this.open({ source: 'icon' });
      return;
    }

    if (!clickedLink && this.cartLink?.href) window.location.assign(this.cartLink.href);
  }

  handleCloseIconKeydown(event) {
    if (
      !this.closeIcon?.classList.contains('active')
      || (event.code !== 'Enter' && event.code !== 'Space')
    ) return;

    event.preventDefault();
    this.close({ restoreFocus: true });
  }

  handleNotificationClick(event) {
    const continueButton = event.target.closest('[data-cart-notification-continue]');
    if (continueButton) {
      this.close({ restoreFocus: true });
      return;
    }

    const thresholdButton = event.target.closest('[data-cart-notification-threshold-info]');
    if (thresholdButton && this.isExpanded()) {
      this.openThresholdDetails(thresholdButton);
      return;
    }

    const quantityButton = event.target.closest('[data-cart-line-action]');
    if (!quantityButton || !this.isExpanded() || this._lineChangePending) return;

    const key = quantityButton.dataset.itemKey;
    if (!key) return;

    const line = quantityButton.closest('[data-cart-notification-line]');
    const currentQuantity = Number(line?.querySelector('[data-cart-line-quantity]')?.textContent) || 1;
    const action = quantityButton.dataset.cartLineAction;
    const quantity = action === 'increase' ? currentQuantity + 1 : action === 'decrease' ? Math.max(currentQuantity - 1, 0) : 0;

    this.changeLineQuantity(key, quantity, quantityButton);
  }

  changeLineQuantity(key, quantity, button) {
    this._lineChangePending = true;
    this.notification.setAttribute('aria-busy', 'true');
    this.setStatus(this.dataset.updatingLabel || 'Updating cart');
    button.disabled = true;

    const focusAction = button.dataset.cartLineAction;
    fetch(window.routes?.cart_change_url || '/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: key, quantity }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart update failed');
        return response.json();
      })
      .then((cart) => this.refreshContents(cart, { key, action: focusAction }))
      .catch(() => {
        this.setError(this.dataset.errorLabel || 'There was an error while updating your cart. Please try again.');
      })
      .finally(() => {
        this._lineChangePending = false;
        this.notification.removeAttribute('aria-busy');
      });
  }

  refreshContents(cart, focusTarget = null) {
    const ids = this.getSectionsToRender().map((section) => section.id);
    return fetch('/?sections=' + ids.join(',') + '&sections_url=' + encodeURIComponent(window.location.pathname))
      .then((response) => {
        if (!response.ok) throw new Error('Section refresh failed');
        return response.json();
      })
      .then((sections) => {
        this.replaceSections(sections);
        this._cartDirty = false;
        this.applyExperienceUI();
        this.updateCartState(cart);
        this.setStatus(this.dataset.updatedLabel || 'Cart updated');

        if (focusTarget) {
          const line = this.findLineByKey(focusTarget.key);
          const nextFocus = line?.querySelector(`[data-cart-line-action="${focusTarget.action}"]`)
            || this.notification.querySelector('[data-cart-line-action]:not(:disabled)')
            || this.notification;
          requestAnimationFrame(() => nextFocus.focus());
        }
      });
  }

  renderContents(parsedState) {
    const update = parsedState.cartUpdate || {
      openNotification: Boolean(parsedState.itemKey || parsedState.variantId),
      itemKey: parsedState.itemKey || null,
      variantId: parsedState.variantId || null,
    };
    const shouldOpen = update.openNotification === true;

    if (shouldOpen) this.lockExperience();
    if (!shouldOpen && !this.isExpanded()) return;

    this.replaceSections(parsedState.sections || parsedState);
    this._cartDirty = false;
    this.applyExperienceUI();

    const addedLines = this.findAddedLines(update);
    this.clearAddedLine();

    if (this.isExpanded()) {
      this.getCartLines().forEach((line) => {
        line.hidden = false;
      });
      this.refreshCartState();
    } else if (shouldOpen && addedLines.length) {
      this.showBaseAddedLines(addedLines, update);
    } else if (shouldOpen) {
      return;
    }

    if (!shouldOpen) return;

    addedLines.forEach((line) => line.classList.add('cart-item--just-added'));
    if (!this.activeElement && document.activeElement !== document.body) this.setActiveElement(document.activeElement);

    this.open({
      source: 'add',
      autoClose: true,
      focus: true,
    });
  }

  replaceSections(sections) {
    this.getSectionsToRender().forEach((section) => {
      const element = document.getElementById(section.id);
      const html = sections?.[section.id];
      if (!element || !html) return;

      const replacement = this.getSectionInnerHTML(html, section.selector);
      if (replacement !== null) element.innerHTML = replacement;
    });
  }

  getSectionsToRender() {
    return [
      { id: 'cart-notification-product' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? null;
  }

  getCartLines() {
    return Array.from(this.querySelectorAll('[data-cart-notification-line]'));
  }

  findLineByKey(key) {
    return this.getCartLines().find((line) => line.dataset.itemKey === String(key)) || null;
  }

  findAddedLines(update) {
    const itemKeys = new Set(
      [...(Array.isArray(update.itemKeys) ? update.itemKeys : []), update.itemKey]
        .filter(Boolean)
        .map(String)
    );
    const variantIds = new Set(
      [...(Array.isArray(update.variantIds) ? update.variantIds : []), update.variantId]
        .filter(Boolean)
        .map(String)
    );
    const cartLines = this.getCartLines();
    const keyedLines = itemKeys.size
      ? cartLines.filter((line) => itemKeys.has(String(line.dataset.itemKey)))
      : [];

    if ((itemKeys.size && keyedLines.length === itemKeys.size) || !variantIds.size) return keyedLines;

    return cartLines.filter((line) => (
      itemKeys.has(String(line.dataset.itemKey))
      || variantIds.has(String(line.dataset.variantId))
    ));
  }

  showBaseAddedLines(addedLines, update) {
    const visibleLines = new Set(addedLines);
    this.getCartLines().forEach((line) => {
      line.hidden = !visibleLines.has(line);
    });

    const count = Number(update.addedItemCount) || addedLines.length;
    const label = document.getElementById('cart-notification-base-count');
    if (label) label.textContent = `${count} ${count === 1 ? 'item' : 'items'} added`;

    const price = document.getElementById('cart-notification-base-price');
    if (!price) return;

    const hasAddedTotal = update.addedTotal !== null
      && update.addedTotal !== undefined
      && Number.isFinite(Number(update.addedTotal));
    price.textContent = addedLines.length > 1 && hasAddedTotal
      ? this.formatMoney(Number(update.addedTotal))
      : addedLines[0]?.dataset.basePrice || '';
  }

  clearAddedLine() {
    this.getCartLines().forEach((line) => line.classList.remove('cart-item--just-added'));
  }

  open({ source = 'programmatic', autoClose = false, focus = false } = {}) {
    if (this.header && typeof this.header.reveal === 'function') this.header.reveal();

    this.updateMobilePosition();
    this.clearAutoClose();
    this._openSource = source;
    this.notification.classList.add('animate', 'active');
    this.notification.classList.toggle('cart-notification--atc-open', source === 'add');
    this.setBaseCloseIconActive(source === 'add' && !this.isExpanded());
    document.body.classList.add('overflow-hidden-mobile');
    document.body.addEventListener('click', this.onBodyClick);
    this.cartLink?.setAttribute('aria-expanded', 'true');

    if (autoClose) this.startAutoClose();

    if (focus) {
      requestAnimationFrame(() => {
        this.updateMobilePosition();
        this.notification.focus();
        trapFocus(this.notification);
      });
    }
  }

  scheduleMobilePositionUpdate() {
    if (
      !this.notification.classList.contains('active')
      || this._mobilePositionFrame !== null
    ) return;

    this._mobilePositionFrame = window.requestAnimationFrame(() => {
      this._mobilePositionFrame = null;
      this.updateMobilePosition();
    });
  }

  updateMobilePosition() {
    if (!window.matchMedia('(max-width: 749px)').matches) {
      this.notification.style.removeProperty('--cart-notification-mobile-top');
      this.notification.style.removeProperty('--cart-notification-mobile-max-height');
      return;
    }

    const headerBoundary = document.querySelector('#shopify-section-header .header-wrapper')
      || document.getElementById('shopify-section-header');
    if (!headerBoundary) return;

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const headerBottom = headerBoundary.getBoundingClientRect().bottom;
    const top = Math.max(0, Math.min(Math.ceil(headerBottom), viewportHeight));
    const availableHeight = Math.max(0, viewportHeight - top);

    this.notification.style.setProperty('--cart-notification-mobile-top', `${top}px`);
    this.notification.style.setProperty('--cart-notification-mobile-max-height', `${availableHeight}px`);
  }

  close({ restoreFocus = false } = {}) {
    if (!this.notification.classList.contains('active')) return;

    this.closeThresholdDetails();
    this.clearAutoClose();
    this.notification.classList.remove('active', 'cart-notification--atc-open');
    this.setBaseCloseIconActive(false);
    document.body.removeEventListener('click', this.onBodyClick);
    document.body.classList.remove('overflow-hidden-mobile');
    this.cartLink?.setAttribute('aria-expanded', 'false');

    const focusTarget = restoreFocus && this.activeElement?.isConnected ? this.activeElement : null;
    removeTrapFocus(focusTarget);
    this._openSource = null;
    this.clearAddedLine();
  }

  setBaseCloseIconActive(active) {
    if (!this.closeIcon) return;

    this.closeIcon.classList.toggle('active', active);
    this.closeIcon.setAttribute('aria-hidden', String(!active));
    this.closeIcon.setAttribute('tabindex', active ? '0' : '-1');
    if (active) {
      this.closeIcon.setAttribute('role', 'button');
    } else {
      this.closeIcon.removeAttribute('role');
    }
  }

  handleBodyClick(event) {
    const target = event.target;
    if (this.notification.contains(target) || target.closest('.header__icon--cart')) {
      this.pauseAutoClose();
      return;
    }
    if (target.closest('.announcement-info-icon')) return;
    if (target.closest('#os-modal-desktop, #os-modal-mobile')) return;
    this.close({ restoreFocus: false });
  }

  markCartDirty() {
    this._cartDirty = true;
  }

  syncExpandedContents() {
    if (!this.isExpanded() || !this._cartDirty) return Promise.resolve();

    const ids = this.getSectionsToRender().map((section) => section.id);
    return fetch('/?sections=' + ids.join(',') + '&sections_url=' + encodeURIComponent(window.location.pathname))
      .then((response) => {
        if (!response.ok) throw new Error('Section refresh failed');
        return response.json();
      })
      .then((sections) => {
        this.replaceSections(sections);
        this._cartDirty = false;
        this.applyExperienceUI();
        return this.refreshCartState();
      })
      .catch(() => {});
  }

  startAutoClose() {
    if (!this.autoClose || !this.timerBar) return;

    this._countdownActive = true;
    this._autoCloseRemaining = this.autoCloseDelay;
    this._autoCloseStartedAt = Date.now();
    this.timerBar.style.animation = 'none';
    this.timerBar.offsetHeight;
    this.timerBar.style.animation = `cartTimer ${this.autoCloseDelay}ms linear forwards`;
    this.timerBar.style.animationPlayState = 'running';
    this.timerBar.classList.add('animate');
    this.autoCloseTimeout = window.setTimeout(() => this.close({ restoreFocus: false }), this.autoCloseDelay);
  }

  pauseAutoClose() {
    if (!this._countdownActive || !this.autoCloseTimeout) return;
    window.clearTimeout(this.autoCloseTimeout);
    this.autoCloseTimeout = null;
    this._autoCloseRemaining -= Date.now() - this._autoCloseStartedAt;
    this._autoCloseRemaining = Math.max(this._autoCloseRemaining, 0);
    if (this.timerBar) this.timerBar.style.animationPlayState = 'paused';
  }

  resumeAutoClose() {
    if (!this._countdownActive || this.autoCloseTimeout) return;
    this._autoCloseStartedAt = Date.now();
    if (this.timerBar) this.timerBar.style.animationPlayState = 'running';
    this.autoCloseTimeout = window.setTimeout(() => this.close({ restoreFocus: false }), this._autoCloseRemaining);
  }

  clearAutoClose() {
    this._countdownActive = false;
    if (this.autoCloseTimeout) window.clearTimeout(this.autoCloseTimeout);
    this.autoCloseTimeout = null;

    if (this.timerBar) {
      this.timerBar.style.animation = 'none';
      this.timerBar.style.animationPlayState = '';
      this.timerBar.classList.remove('animate');
    }
  }

  refreshCartState() {
    if (!this.isExpanded()) return Promise.resolve(null);
    if (this._cartStatePromise) return this._cartStatePromise;

    this._cartStatePromise = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error('Cart request failed');
        return response.json();
      })
      .then((cart) => {
        this.updateCartState(cart);
        return cart;
      })
      .catch(() => null)
      .finally(() => {
        this._cartStatePromise = null;
      });

    return this._cartStatePromise;
  }

  updateCartState(cart) {
    if (!cart) return;
    this._cartState = cart;

    const count = document.getElementById('cart-notification-item-count');
    const total = document.getElementById('cart-notification-price');
    if (count) {
      const unit = cart.item_count === 1 ? count.dataset.unitOne || 'item' : count.dataset.unitOther || 'items';
      count.textContent = `(${cart.item_count} ${unit})`;
    }
    if (total) total.textContent = this.formatMoney(cart.total_price);

    this.renderThreshold(cart);

    document.dispatchEvent(new CustomEvent('theme:cart-updated', {
      detail: { cart },
    }));
  }

  initializeThreshold() {
    if (this._thresholdInitialized || !this.isExpanded()) return;
    this._thresholdInitialized = true;

    const dataElement = document.getElementById('threshold-data');
    const container = document.getElementById('cart-notification-progress');
    if (!dataElement || !container) return;

    try {
      this._thresholdData = JSON.parse(dataElement.textContent);
    } catch (error) {
      this._thresholdData = null;
    }

    if (!Number(this._thresholdData?.threshold_price)) {
      this._thresholdData = null;
      return;
    }

    this.refreshCartState();
  }

  renderThreshold(cart) {
    if (!this.isExpanded() || !this._thresholdData) return;

    const container = document.getElementById('cart-notification-progress');
    if (!container) return;
    if (!cart.item_count) {
      container.hidden = true;
      return;
    }

    const threshold = Number(this._thresholdData.threshold_price);
    const cartTotal = cart.total_price / 100;
    const message = container.querySelector('.threshold-text');
    const fill = container.querySelector('.threshold-fill');
    const amount = container.querySelector('.threshold-amount');
    const progressWrapper = container.querySelector('.threshold-bar-amount-wrapper');
    if (!message || !fill || !amount || !progressWrapper) return;

    fill.style.width = `${Math.min((cartTotal / threshold) * 100, 100)}%`;
    amount.textContent = this.formatMoney(Math.round(threshold * 100));

    if (cartTotal < threshold) {
      const difference = this.formatMoney(Math.round((threshold - cartTotal) * 100));
      const promotionMessage = this._thresholdData.promotion_message || '';
      const placement = this._thresholdData.threshold_placement;
      message.innerHTML = placement && promotionMessage.includes(placement)
        ? promotionMessage.replace(placement, `${placement} <strong>${difference}</strong> `)
        : promotionMessage.replace(/\{\{amount\}\}/, difference);
      progressWrapper.hidden = false;
    } else {
      message.innerHTML = `<span aria-hidden="true">&#10003;</span> ${this._thresholdData.threshold_met || ''}`;
      progressWrapper.hidden = true;
    }

    if (message.innerHTML.includes('|')) message.innerHTML = message.innerHTML.split('|').map((part) => part.trim()).join('<br>');
    container.hidden = false;
  }

  openThresholdDetails(button) {
    const announcementTrigger = document.querySelector('#announcement-bar .announcement-info-icon');
    if (!announcementTrigger) return;

    if (window.innerWidth < 600) {
      this._thresholdDetailsOpen = true;
      announcementTrigger.click();
      return;
    }

    const modal = document.getElementById('os-modal-desktop');
    if (!modal) return;

    this.bindThresholdDetailsHover(modal);
    this.clearThresholdDetailsWait();
    this._thresholdDetailsOpen = true;
    modal.style.visibility = 'hidden';

    const positionAndReveal = () => {
      if (window.getComputedStyle(modal).display === 'none') return false;

      this.clearThresholdDetailsWait();
      if (!this._thresholdDetailsOpen) {
        this.hideThresholdDetailsElements();
        return true;
      }

      const rect = button.getBoundingClientRect();
      const width = modal.offsetWidth || 400;
      const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
      modal.style.position = 'fixed';
      modal.style.top = `${rect.bottom + 10}px`;
      modal.style.left = `${left}px`;
      modal.style.visibility = 'visible';
      return true;
    };

    this._thresholdDetailsObserver = new MutationObserver(() => positionAndReveal());
    this._thresholdDetailsObserver.observe(modal, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    announcementTrigger.click();

    if (positionAndReveal()) return;
    this._thresholdDetailsFallback = window.setTimeout(() => {
      this._thresholdDetailsFallback = null;
      if (positionAndReveal()) return;

      this.clearThresholdDetailsWait();
      this._thresholdDetailsOpen = false;
      this.hideThresholdDetailsElements();
    }, 750);
  }

  closeThresholdDetails() {
    this._thresholdDetailsOpen = false;

    const mobileModal = document.getElementById('os-modal-mobile');
    if (mobileModal) mobileModal.style.display = 'none';

    const desktopModal = document.getElementById('os-modal-desktop');
    if (this._thresholdDetailsObserver && desktopModal) {
      // Keep the pending popup hidden until the announcement handler finishes;
      // the observer will then remove its display/classes without a flash.
      desktopModal.style.visibility = 'hidden';
      return;
    }

    this.clearThresholdDetailsWait();
    this.hideThresholdDetailsElements();
  }

  clearThresholdDetailsWait() {
    this._thresholdDetailsObserver?.disconnect();
    this._thresholdDetailsObserver = null;

    if (this._thresholdDetailsFallback) {
      window.clearTimeout(this._thresholdDetailsFallback);
      this._thresholdDetailsFallback = null;
    }
  }

  hideThresholdDetailsElements() {
    const desktopModal = document.getElementById('os-modal-desktop');
    if (desktopModal) {
      desktopModal.style.display = 'none';
      desktopModal.style.removeProperty('visibility');
      desktopModal.classList.remove('show', 'announcement-popup');
    }

    document.getElementById('parent-container')?.classList.remove('announcement-popup-container');

    const mobileModal = document.getElementById('os-modal-mobile');
    if (mobileModal) mobileModal.style.display = 'none';
  }

  formatMoney(cents) {
    if (window.theme?.Currency?.formatMoney) return window.theme.Currency.formatMoney(cents);
    return `$${(Number(cents) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  }

  setStatus(message) {
    const status = document.getElementById('cart-notification-status');
    if (status) status.textContent = message;
    this.setError('');
  }

  setError(message) {
    const error = document.getElementById('cart-notification-errors');
    if (error) error.textContent = message;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}
